// Wandering townsfolk for the Capital — for now, Samurai Warriors, so character
// PROPORTIONS can be compared against the cat hero, the houses and each other.
//
// Each NPC is a skinned model (own skeleton via SkeletonUtils.clone) with an authored
// Idle + Walk cycle, living in the capital root's local space (source METRES — the same
// space as the collision data). They pick a random spot nearby, walk there in a straight
// line (stopping if they'd hit a building), pause, and repeat.

import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js'
import { CapitalConfig as CFG } from '../config/capital.js'

const WALK_CYCLE_SPEED = 1.5 // m/s the authored walk cycle is built for (no foot sliding at this speed)

export async function loadNpcAssets(baseDir) {
  const dir = `${baseDir}/chars`
  const gltf = await new GLTFLoader().loadAsync(`${dir}/warrior.glb`)
  const textures = await Promise.all([1, 2, 3].map((n) => new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(`${dir}/Characters_Texture_0${n}.png`, (t) => {
      t.colorSpace = THREE.SRGBColorSpace
      t.flipY = false
      t.magFilter = THREE.NearestFilter
      t.minFilter = THREE.NearestFilter
      t.generateMipmaps = false
      resolve(t)
    }, undefined, reject)
  })))
  const ninja = await new GLTFLoader().loadAsync(`${dir}/ninja.glb`).catch(() => null) // thief is optional
  return { gltf, textures, ninja }
}

const rand = (a, b) => a + Math.random() * (b - a)

// stepM(x, z, y) -> new floor height | null : may a body stand at (x,z) arriving from height y?
export function createNpcs({ parent, assets, stepM, centre }) {
  const { gltf, textures } = assets
  const clipByName = (n) => gltf.animations.find((c) => c.name.toLowerCase() === n)
  const walkClip = clipByName('walk')
  const idleClip = clipByName('idle')
  const group = new THREE.Group()
  group.name = 'capital-npcs'
  parent.add(group)

  // Free spot near (cx,cz) metres, at floor height ~cy, that a body could stand on.
  const freeSpot = (cx, cz, cy, rMin, rMax, tries = 40) => {
    for (let i = 0; i < tries; i++) {
      const a = Math.random() * Math.PI * 2
      const r = rand(rMin, rMax)
      const x = cx + Math.cos(a) * r
      const z = cz + Math.sin(a) * r
      const y = stepM(x, z, cy)
      if (y !== null && Math.abs(y - cy) < 2.5) return { x, z, y }
    }
    return null
  }
  // A straight walk from (x,z,y) to (tx,tz) is clear all the way (sampled every ~0.4 m, following the floor).
  const pathClear = (x, z, y, tx, tz) => {
    const d = Math.hypot(tx - x, tz - z)
    const steps = Math.ceil(d / 0.4)
    let cy = y
    for (let i = 1; i <= steps; i++) {
      const t = i / steps
      const ny = stepM(x + (tx - x) * t, z + (tz - z) * t, cy)
      if (ny === null) return false
      cy = ny
    }
    return true
  }

  const npcs = []
  for (let i = 0; i < CFG.npcCount; i++) {
    const start = freeSpot(centre.x, centre.z, centre.y, 4, 16) ?? { x: centre.x, z: centre.z, y: centre.y }
    const holder = new THREE.Group()
    const model = cloneSkinned(gltf.scene)
    model.scale.setScalar(CFG.npcScale)
    // Each warrior gets one of the three colourways (white / brown / black armour).
    const mat = new THREE.MeshLambertMaterial({ map: textures[i % textures.length], side: THREE.DoubleSide })
    mat.color.setScalar(CFG.brightness)
    model.traverse((o) => {
      if (o.isMesh || o.isSkinnedMesh) { o.material = mat; o.frustumCulled = false }
    })
    holder.add(model)
    holder.position.set(start.x, start.y, start.z) // feet on the floor
    holder.rotation.y = Math.random() * Math.PI * 2
    group.add(holder)

    const mixer = new THREE.AnimationMixer(model)
    const walk = walkClip ? mixer.clipAction(walkClip) : null
    const idle = idleClip ? mixer.clipAction(idleClip) : null
    walk?.setEffectiveTimeScale(CFG.npcSpeed / WALK_CYCLE_SPEED)
    const npc = {
      holder, mixer, walk, idle, current: null,
      mode: 'idle', timer: rand(0.2, 2.5), tx: start.x, tz: start.z, heading: holder.rotation.y,
    }
    const t0 = Math.random()
    ;[walk, idle].forEach((a) => { if (a) { a.play(); a.time = t0 * a.getClip().duration; a.setEffectiveWeight(0) } })
    play(npc, idle ?? walk, 0)
    npcs.push(npc)
  }

  function play(npc, next, fade = 0.25) {
    if (!next || npc.current === next) return
    next.setEffectiveWeight(1)
    if (npc.current) { next.reset().fadeIn(fade).play(); npc.current.fadeOut(fade) } else next.play()
    npc.current = next
  }

  function pickTarget(npc) {
    const { x, y, z } = npc.holder.position
    for (let i = 0; i < 8; i++) {
      const p = freeSpot(x, z, y, 5, 20, 6)
      if (p && pathClear(x, z, y, p.x, p.z)) { npc.tx = p.x; npc.tz = p.z; return true }
    }
    return false
  }

  function tick(dt) {
    for (const n of npcs) {
      n.mixer.update(dt)
      const h = n.holder
      if (n.mode === 'idle') {
        n.timer -= dt
        if (n.timer <= 0) {
          if (pickTarget(n)) { n.mode = 'walk'; play(n, n.walk ?? n.idle) } else n.timer = rand(0.6, 1.5)
        }
      } else {
        const dx = n.tx - h.position.x
        const dz = n.tz - h.position.z
        const d = Math.hypot(dx, dz)
        if (d < 0.35) { n.mode = 'idle'; n.timer = rand(1.5, 5); play(n, n.idle ?? n.walk); continue }
        // Turn toward the heading (shortest way round), then step.
        const want = Math.atan2(dx, dz)
        let diff = want - n.heading
        diff = Math.atan2(Math.sin(diff), Math.cos(diff))
        n.heading += diff * Math.min(1, 7 * dt)
        h.rotation.y = n.heading
        const step = Math.min(CFG.npcSpeed * dt, d)
        const nx = h.position.x + (dx / d) * step
        const nz = h.position.z + (dz / d) * step
        const ny = stepM(nx, nz, h.position.y)
        if (ny !== null) { h.position.x = nx; h.position.z = nz; h.position.y += (ny - h.position.y) * Math.min(1, 14 * dt) }
        else { n.mode = 'idle'; n.timer = rand(0.4, 1.2); play(n, n.idle ?? n.walk) } // blocked: stop and re-plan
      }
    }
  }

  const debug = () => npcs.map((n) => ({ x: +n.holder.position.x.toFixed(2), z: +n.holder.position.z.toFixed(2), mode: n.mode }))
  // Rendered height of one warrior in WORLD units (for comparing against the cat hero).
  const heightWorld = () => {
    if (!npcs.length) return 0
    npcs[0].holder.updateMatrixWorld(true)
    const sm = []
    npcs[0].holder.traverse((o) => { if (o.isSkinnedMesh) sm.push(o) })
    const box = new THREE.Box3()
    for (const m of sm) { m.computeBoundingBox?.(); box.union(m.boundingBox.clone().applyMatrix4(m.matrixWorld)) }
    return box.max.y - box.min.y
  }
  const positions = () => npcs.map((n) => ({ x: n.holder.position.x, z: n.holder.position.z, kind: 'warrior' }))
  return { tick, debug, heightWorld, positions, count: npcs.length }
}
